import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGetFormFields } from "@/hooks";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { useCallback, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { FormAutocomplete } from "./form-autocomplete";
import { AggregationType, FormData } from "@/types/dashboard.types";
import { TrendingUp, Hash, Grid } from "lucide-react";

interface CrosstabConfig {
  row: {
    formId: string;
    fieldId?: string;
    systemField?: string;
    includeMissing: boolean;
  };
  column: {
    formId: string;
    fieldId?: string;
    systemField?: string;
    includeMissing: boolean;
  };
  value: {
    formId: string;
    fieldId?: string;
    systemField?: string;
    aggregation: string;
  };
  rowAxisTitle?: string;
  colAxisTitle?: string;
}

interface CrosstabMetricEditorProps {
  config: CrosstabConfig;
  onChange: (config: CrosstabConfig) => void;
  availableForms: FormData[];
}

function FieldSelector({
  label,
  value,
  formId,
  onChange,
  showIncludeMissing = false,
  includeMissing = false,
  onIncludeMissingChange,
}: {
  label: string;
  value: { fieldId?: string; systemField?: string };
  formId: string;
  onChange: (value: { fieldId?: string; systemField?: string }) => void;
  showIncludeMissing?: boolean;
  includeMissing?: boolean;
  onIncludeMissingChange?: (checked: boolean) => void;
}) {
  const { t } = useTranslation();
  const { data: formFieldsData, isLoading } = useGetFormFields(formId);

  const availableFields = useMemo(() => {
    if (!formId || !formFieldsData) return [];

    const systemFields = [
      { id: "$responseId$", label: "Response ID", type: "categorical" },
      { id: "$submissionDate$", label: "Submission Date", type: "datetime" },
    ];

    const formFields = formFieldsData?.formFields || [];
    return [...systemFields, ...formFields];
  }, [formId, formFieldsData]);

  if (!formId) {
    return (
      <Select disabled>
        <SelectTrigger>
          <SelectValue placeholder={t('processManagement.selectFormFirst')} />
        </SelectTrigger>
      </Select>
    );
  }

  if (isLoading) {
    return (
      <Select disabled>
        <SelectTrigger>
          <SelectValue placeholder={t('processManagement.loadingFields')} />
        </SelectTrigger>
      </Select>
    );
  }

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select
        value={value.systemField || value.fieldId || ""}
        onValueChange={(selectedValue) => {
          if (selectedValue.startsWith("$")) {
            onChange({ systemField: selectedValue, fieldId: undefined });
          } else {
            onChange({ fieldId: selectedValue, systemField: undefined });
          }
        }}
      >
        <SelectTrigger>
          <SelectValue placeholder={t('processManagement.selectField')} />
        </SelectTrigger>
        <SelectContent>
          {availableFields.map((field) => (
            <SelectItem key={`${formId}-${field.id}`} value={field.id!}>
              {field.id!.startsWith("$") ? (
                <div className="flex items-center gap-2">
                  <span className="text-blue-600">📊</span>
                  {field.label} ({t('processManagement.system')})
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  {field.label} ({field.type})
                </div>
              )}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {showIncludeMissing && onIncludeMissingChange && (
        <div className="flex items-center space-x-2">
          <Checkbox
            id={`${label}-include-missing`}
            checked={includeMissing}
            onCheckedChange={(checked) => onIncludeMissingChange(!!checked)}
          />
          <label
            htmlFor={`${label}-include-missing`}
            className="text-sm cursor-pointer"
          >
            {t('processManagement.includeMissingValues')}
          </label>
        </div>
      )}
    </div>
  );
}

export function CrosstabMetricEditor({
  config,
  onChange,
  availableForms,
}: CrosstabMetricEditorProps) {
  const { t } = useTranslation();

  const updateConfig = useCallback((updates: Partial<CrosstabConfig>) => {
    const newConfig = { ...config, ...updates };
    onChange(newConfig);
  }, [config, onChange]);

  const updateRow = useCallback((updates: Partial<CrosstabConfig['row']>) => {
    updateConfig({ row: { ...config.row, ...updates } });
  }, [config.row, updateConfig]);

  const updateColumn = useCallback((updates: Partial<CrosstabConfig['column']>) => {
    updateConfig({ column: { ...config.column, ...updates } });
  }, [config.column, updateConfig]);

  const updateValue = useCallback((updates: Partial<CrosstabConfig['value']>) => {
    updateConfig({ value: { ...config.value, ...updates } });
  }, [config.value, updateConfig]);

  const { data: valueFormFieldsData } = useGetFormFields(config.value.formId);
  const valueFormFields = valueFormFieldsData?.formFields || [];

  const isValueFieldNumeric = useMemo(() => {
    if (!config.value.fieldId) return false;
    const field = valueFormFields.find((f: any) => f.id === config.value.fieldId);
    return field?.type === "number" || field?.type === "currency" || field?.type === "decimal" || field?.type === "integer";
  }, [config.value.fieldId, valueFormFields]);

  const availableAggregations: AggregationType[] = isValueFieldNumeric
    ? ["count", "sum", "mean", "median", "mode", "min", "max", "std", "variance", "p10", "p25", "p50", "p75", "p90"]
    : ["count", "mode"];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Grid className="w-5 h-5" />
          {t('processManagement.crosstabConfiguration')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Row Configuration */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Hash className="w-4 h-4" />
            <Label className="text-sm font-medium">{t('processManagement.rowCategory')}</Label>
          </div>

          <div className="space-y-2">
            <Label>{t('processManagement.form')}</Label>
            <FormAutocomplete
              key={`row-form-${config.row.formId}`}
              value={config.row.formId}
              onValueChange={(formId) => {
                const newFormId = formId || "";
                onChange({
                  row: {
                    formId: newFormId,
                    fieldId: undefined,
                    systemField: undefined,
                    includeMissing: config.row?.includeMissing ?? false,
                  },
                  column: {
                    formId: newFormId,
                    fieldId: undefined,
                    systemField: undefined,
                    includeMissing: config.column?.includeMissing ?? false,
                  },
                  value: {
                    formId: newFormId,
                    fieldId: undefined,
                    systemField: undefined,
                    aggregation: (config.value?.aggregation as AggregationType) || "count",
                  },
                  rowAxisTitle: config.rowAxisTitle || "",
                  colAxisTitle: config.colAxisTitle || "",
                });
              }}
              forms={availableForms.map(f => ({
                id: f.id,
                title: f.title,
                name: f.name || f.title || "",
                description: f.description,
              }))}
              placeholder={t('processManagement.searchForms')}
            />
          </div>

          <FieldSelector
            label={t('processManagement.field')}
            value={{ fieldId: config.row.fieldId, systemField: config.row.systemField }}
            formId={config.row.formId}
            onChange={(value) => updateRow(value)}
            showIncludeMissing={true}
            includeMissing={config.row.includeMissing}
            onIncludeMissingChange={(checked) => updateRow({ includeMissing: checked })}
          />

          <div className="space-y-2">
            <Label>{t('processManagement.rowAxisTitle')}</Label>
            <Input
              value={config.rowAxisTitle || ''}
              onChange={(e) => updateConfig({ rowAxisTitle: e.target.value })}
              placeholder={t('processManagement.rowAxisTitle')}
            />
          </div>
        </div>
       <div className="border-t-4 border-gray-200 my-6" />
       {/* Column Configuration */}
       <div className="space-y-4">
         <div className="flex items-center gap-2">
           <Hash className="w-4 h-4" />
           <Label className="text-sm font-medium">{t('processManagement.columnCategory')}</Label>
         </div>

         <div className="space-y-2">
           <Label>{t('processManagement.form')}</Label>
           <div className="px-3 py-2 border rounded-md bg-muted text-sm">
             {availableForms.find(f => f.id === config.column.formId)?.name ||
               availableForms.find(f => f.id === config.column.formId)?.title ||
               config.column.formId}
           </div>
         </div>

         <FieldSelector
           label={t('processManagement.field')}
           value={{ fieldId: config.column.fieldId, systemField: config.column.systemField }}
           formId={config.column.formId}
           onChange={(value) => updateColumn(value)}
           showIncludeMissing={true}
           includeMissing={config.column.includeMissing}
           onIncludeMissingChange={(checked) => updateColumn({ includeMissing: checked })}
         />

         <div className="space-y-2">
           <Label>{t('processManagement.colAxisTitle')}</Label>
           <Input
             value={config.colAxisTitle || ''}
             onChange={(e) => updateConfig({ colAxisTitle: e.target.value })}
             placeholder={t('processManagement.colAxisTitle')}
           />
         </div>
       </div>

       <div className="border-t-4 border-gray-200 my-6" />

        {/* Value Configuration */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            <Label className="text-sm font-medium">{t('processManagement.valueMetric')}</Label>
          </div>

          <div className="space-y-2">
            <Label>{t('processManagement.form')}</Label>
            <div className="px-3 py-2 border rounded-md bg-muted text-sm">
              {availableForms.find(f => f.id === config.value.formId)?.name ||
               availableForms.find(f => f.id === config.value.formId)?.title ||
               config.value.formId}
            </div>
          </div>

          <FieldSelector
            label={t('processManagement.field')}
            value={{ fieldId: config.value.fieldId, systemField: config.value.systemField }}
            formId={config.value.formId}
            onChange={(value) => updateValue(value)}
          />

          <div className="space-y-2">
            <Label>{t('processManagement.aggregation')}</Label>
            <Select
              value={config.value.aggregation}
              onValueChange={(aggregation: AggregationType) =>
                updateValue({ aggregation })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableAggregations.map((agg) => (
                  <SelectItem key={agg} value={agg}>
                    {agg.charAt(0).toUpperCase() + agg.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>


        {/* Preview */}
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>{t('processManagement.preview')}:</strong>{" "}
            {t('processManagement.crosstabOf')} {config.value.aggregation} {t('processManagement.of')} {
              config.value.systemField ||
              valueFormFields.find((f: any) => f.id === config.value.fieldId)?.label ||
              "field"
            } {t('processManagement.by')} {
              config.row.systemField ||
              valueFormFields.find((f: any) => f.id === config.row.fieldId)?.label ||
              "row field"
            } × {
              config.column.systemField ||
              valueFormFields.find((f: any) => f.id === config.column.fieldId)?.label ||
              "column field"
            }
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
